import { NgkAuthService } from '@kin/ngk-auth';
import { NgKLoggerService } from '@kin/ngk-logger';
import { Observable, catchError, filter, forkJoin, from, of, take, tap } from 'rxjs';

import { AppApiRootList, AppEnvironment } from 'app/app-config/app-config.model';
import { AppConfigService } from 'app/app-config/app-config.service';
import { SESSION_STORAGE_HOME_BASE_URL_KEY, SESSION_STORAGE_PLATFORM_BASE_URL_KEY } from 'app/app-config/app-init/session-storage-keys';
import { intakeProdApiRoot, opportunitiesProdApiRoot, quotingAppApiConfig, quotingPlatformProdApiRoot, quotingProdApiRoot } from 'app/app-config/configurations/api-config';
import { buildAuthConfig } from 'app/app-config/configurations/auth-config';
import { getAppEnvironment } from 'app/app-config/configurations/env-config';
import { fullstoryEnvConfig } from 'app/app-config/configurations/fullstory-config';
import { mediaAlphaAPIToken, mediaAlphaAPIUrl, mediaAlphaPlacementKeyOOM, mediaAlphaPlacementKeyUWF } from 'app/app-config/configurations/mediaalpha-config';
import { smartyStreetsEnvConfig } from 'app/app-config/configurations/smartyStreets-config';
import { splitIOEnvConfig } from 'app/app-config/configurations/splitIO-config';
import { FullstoryService } from 'app/shared/services/fullstory/fullstory.service';
import { GuidService } from 'app/shared/services/guid/guid.service';
import { SplitIOService } from 'app/shared/services/splitIO/splitIO.service';
import { stripTrailingSlash } from 'app/shared/utils/url/url-helpers';

import pkgJson from '../../../../package.json';

const IDLE_TIMEOUT_SECONDS = 1500; // 25 minutes

type ResolvedContext = {
  env: AppEnvironment;
  apiRoot: AppApiRootList;
};

export function appInitializerFactory(
  appConfigService: AppConfigService,
  injectedWindow: Window,
  logger: NgKLoggerService,
  fullstoryService: FullstoryService,
  guidService: GuidService,
  splitIOService: SplitIOService,
  sessionStorage: Storage | null,
  authService: NgkAuthService,
): () => Observable<unknown> {
  return () => {
    const clientHost = injectedWindow?.location?.host; // get client host the app was loaded into
    const env = getAppEnvironment(clientHost); // associate with a known env
    const apiRoot = quotingAppApiConfig.get(env!);
    const buildVersion = pkgJson.version ?? '0.0.0-unknown';

    // URLs where we log to datadog as opposed to just the console; do not enable for localhost or preview envs
    const activeDatadogUrls = ['quoting.kin.insure', 'welcome.kin.com', 'quoting.kin.haus', 'welcome.kin.haus'];

    let resolvedContext: ResolvedContext;

    if (!env || !apiRoot) {
      // Force datadog to be active since we are defaulting to production
      initLogger(logger, [clientHost], buildVersion);
      // If either env or apiRoot are undefined, we don't know where the app was loaded. Throw error and default to production;
      // this needs to be after we init logger so we get the unknown env alert
      logger.error({
        priority: 'P1',
        context: 'AppInitializerFactory',
        message: `Unknown Environment Detected - Host: ${clientHost}, Origin: ${window.location.origin}, Location: ${JSON.stringify(window.location)}`,
      });

      resolvedContext = defaultToProduction(logger);
    } else {
      resolvedContext = { env, apiRoot: { ...apiRoot } };
      initLogger(logger, activeDatadogUrls, buildVersion);
    }

    const fullstoryOrgID = fullstoryEnvConfig.get(resolvedContext.env)!;
    fullstoryService.initialize(resolvedContext.env, fullstoryOrgID, guidService.guid);

    const splitIOKey = splitIOEnvConfig.get(resolvedContext.env)!;
    splitIOService.createSplitIOClient({ splitIOClientKey: splitIOKey, userIdentifier: guidService.guid });

    // If user has updated the API root URLs through the Debug Panel, use those values pulled from session storage.
    // Otherwise, use the values from the environment config.
    if (resolvedContext.env !== 'production' && sessionStorage) {
      const homeBaseUrlFromSessionStorage = sessionStorage.getItem(SESSION_STORAGE_HOME_BASE_URL_KEY);
      const platformBaseUrlFromSessionStorage = sessionStorage.getItem(SESSION_STORAGE_PLATFORM_BASE_URL_KEY);

      if (homeBaseUrlFromSessionStorage) {
        const baseUrl = stripTrailingSlash(homeBaseUrlFromSessionStorage);
        resolvedContext.apiRoot.quoteV1 = `${baseUrl}/api/v1`;
        resolvedContext.apiRoot.quotingPlatform = `${baseUrl}/api/v1/shim`;
      }

      if (platformBaseUrlFromSessionStorage) {
        const baseUrl = stripTrailingSlash(platformBaseUrlFromSessionStorage);
        resolvedContext.apiRoot.opportunities = `${baseUrl}/opportunities`;
        resolvedContext.apiRoot.intake = `${baseUrl}/intake`;
      }
    }

    // Add setting to the base configuration
    appConfigService.setStartupContext({
      buildVersion,
      environment: resolvedContext.env,
      apiRoot: resolvedContext.apiRoot,
      notProd: resolvedContext.env !== 'production',
      smartyStreetsKey: smartyStreetsEnvConfig.get(resolvedContext.env)!,
      fullstoryOrgID,
      splitIOKey,
      mediaAlphaAPIToken: mediaAlphaAPIToken.get(resolvedContext.env)!,
      mediaAlphaPlacementKeyUWF: mediaAlphaPlacementKeyUWF.get(resolvedContext.env)!,
      mediaAlphaPlacementKeyOOM: mediaAlphaPlacementKeyOOM.get(resolvedContext.env)!,
      mediaAlphaAPIURL: mediaAlphaAPIUrl.get(resolvedContext.env)!,
    });

    const authConfig = buildAuthConfig(resolvedContext.env, resolvedContext.apiRoot, injectedWindow.location.origin);
    const authInitPromise = authService.initialize(authConfig, { idleTimeout: IDLE_TIMEOUT_SECONDS }).catch((error) => {
      logger.error({
        message: 'Failed to initialize NgkAuthService',
        priority: 'P1',
        context: 'appInitializerFactory',
        additionalInfo: { message: (error as Error).message, stack: (error as Error).stack },
      });
    });

    return forkJoin([
      splitIOService.clientStatus$.pipe(
        tap((status) => {
          if (status === 'failed') {
            logger.error({ message: 'SplitIO client failed to initialize', priority: 'P3', context: 'appInitializerFactory' });
          }
        }),
        filter((status) => status === 'ready' || status === 'failed'),
        take(1),
        catchError(() => of(true)),
      ),
      from(authInitPromise),
    ]);
  };
}

/**
 * Abstracted function to clarify logic for defaulting to production
 */
function defaultToProduction(logger: NgKLoggerService): ResolvedContext {
  logger.error({ message: 'Unknown Host, Using Production', priority: 'P3', context: 'appInitializerFactory' });
  return {
    env: 'production',
    apiRoot: {
      quoteV1: quotingProdApiRoot,
      quotingPlatform: quotingPlatformProdApiRoot,
      opportunities: opportunitiesProdApiRoot,
      intake: intakeProdApiRoot,
    },
  };
}

function initLogger(logger: NgKLoggerService, activeUrls: string[], buildVersion: string) {
  // Configure Datadog RUM
  logger.initializeDataDog({
    applicationId: 'bc4c19ab-44cc-482b-8a1f-8ccd9d1f1083',
    clientToken: 'pub680814101f24f903b1d14a63f78efcca',
    service: 'quoting-ui',
    version: buildVersion,
    activeUrls,
    allowedTracingUrls: [
      'https://quoting.kin.insure',
      'https://welcome.kin.com',
      'https://quoting.kin.haus',
      'https://welcome.kin.haus',
      'https://app.kin.insure',
      'https://app.kin.com',
      'https://app.kin.haus',
    ],
    traceSampleRate: 100,
  });
}
